import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, AlertTriangle, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { Party, PartyCreate, PartyGroup, PartyUpdate } from '@/app/types/party';
import { Site } from '@/app/types/site';
import { listParties, createParty, listPartyGroups, getParty } from '@/app/services/partyService';
import { listSites } from '@/app/services/siteService';
import { debounce } from 'lodash';
import { PaginatedResponse } from '@/app/types/api';
import PartyForm from '@/app/pages/party/components/PartyForm';
import { toast } from 'sonner';
import { usePreferenceStore } from '@/app/store/preferenceStore';

interface PartySelectorProps {
    value: number | undefined;
    onChange: (value: number | undefined) => void;
    placeholder?: string;
    filterPartyType?: 'customer' | 'vendor';
    filterPartyGroup?: string; // Filter by party group name (e.g., "EMPLOYEE")
    disabled?: boolean;
}

export const PartySelector: React.FC<PartySelectorProps> = ({
    value,
    onChange,
    placeholder = "Select party...",
    filterPartyType,
    filterPartyGroup,
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isPartyFormOpen, setIsPartyFormOpen] = useState(false);
    const queryClient = useQueryClient();
    const { defaultSiteId } = usePreferenceStore();

    const debouncedSetSearchQuery = useMemo(() => debounce(setSearchQuery, 300), []);

    // Fetch party groups to get the ID for filtering
    const partyGroupsForFilterQuery = useQuery<PaginatedResponse<PartyGroup>, Error>({
        queryKey: ['partyGroupsForFilter', filterPartyGroup],
        queryFn: () => listPartyGroups({
            filters: JSON.stringify([{ field: "name", op: "ilike", value: filterPartyGroup }]),
            limit: 1
        }),
        enabled: !!filterPartyGroup,
        staleTime: 15 * 60 * 1000,
    });

    const partyGroupId = useMemo(() => {
        if (!filterPartyGroup) return undefined;
        return partyGroupsForFilterQuery.data?.items[0]?.id;
    }, [filterPartyGroup, partyGroupsForFilterQuery.data]);

    const buildPartyFilters = (): string | undefined => {
        const filters: { field: string; op: string; value: unknown }[] = [];
        if (filterPartyType) {
            filters.push({ field: "party_type", op: "eq", value: filterPartyType });
        }
        if (filterPartyGroup && partyGroupId) {
            filters.push({ field: "party_group_id", op: "eq", value: partyGroupId });
        }
        filters.push({ field: "is_active", op: "eq", value: true });
        return filters.length > 0 ? JSON.stringify(filters) : undefined;
    };

    const partiesQuery = useQuery<PaginatedResponse<Party>, Error>({
        queryKey: ['parties', { filters: buildPartyFilters(), search: searchQuery, limit: 100 }],
        queryFn: () => listParties({
            filters: buildPartyFilters(),
            search_query: searchQuery || undefined,
            limit: 100,
            sort_by: 'name',
            sort_dir: 'asc'
        }),
        // Only fetch when open or have value, AND if filtering by group, wait until we have the group ID
        enabled: (open || !!value) && (!filterPartyGroup || !!partyGroupId),
        staleTime: 5 * 60 * 1000,
    });

    // Separate query to fetch the selected party if not in the list
    const selectedPartyQuery = useQuery<Party, Error>({
        queryKey: ['party', value],
        queryFn: () => getParty(value!),
        enabled: !!value && !partiesQuery.data?.items.find(p => p.id === value),
        staleTime: 5 * 60 * 1000,
    });

    const partyGroupsQuery = useQuery<PaginatedResponse<PartyGroup>, Error>({
        queryKey: ['partyGroupsForSelector'],
        queryFn: () => listPartyGroups({ limit: 200, sort_by: 'name', sort_dir: 'asc' }),
        staleTime: 15 * 60 * 1000,
        enabled: isPartyFormOpen,
    });

    const sitesQuery = useQuery<PaginatedResponse<Site>, Error>({
        queryKey: ['sitesForSelector'],
        queryFn: () => listSites({ limit: 100, show_all: true }),
        staleTime: 15 * 60 * 1000,
        enabled: isPartyFormOpen,
    });

    const parties = partiesQuery.data?.items ?? [];
    const selectedParty = useMemo(() => {
        // First try to find in the parties list
        const partyFromList = parties.find(p => p.id === value);
        if (partyFromList) return partyFromList;
        
        // If not found in list, use the individually fetched party
        return selectedPartyQuery.data;
    }, [parties, value, selectedPartyQuery.data]);

    const createPartyMutation = useMutation({
        mutationFn: (data: PartyCreate) => createParty(data),
        onSuccess: (newParty) => {
            toast.success(`Party "${newParty.name}" created successfully!`);
            queryClient.invalidateQueries({ queryKey: ['parties'] });
            onChange(newParty.id);
            setIsPartyFormOpen(false);
            setOpen(false);
        },
        onError: (error: Error) => {
            const errorMsg = error?.message || "Unknown error";
            toast.error(`Failed to create party: ${errorMsg}`);
        },
    });

    const handleCreateNewPartySubmit = (data: PartyCreate | PartyUpdate): void => {
        createPartyMutation.mutate(data as PartyCreate);
    };

    const handleOpenPartyForm = (): void => {
        setOpen(false);
        setIsPartyFormOpen(true);
    };

    // Ensure the selected party is in the dropdown list
    const displayParties = useMemo(() => {
        if (!selectedParty || parties.find(p => p.id === selectedParty.id)) {
            return parties;
        }
        // Add the selected party to the beginning of the list if it's not there
        return [selectedParty, ...parties];
    }, [parties, selectedParty]);

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className={cn("w-full justify-between", !value && "text-muted-foreground")}
                        disabled={disabled || createPartyMutation.isPending}
                    >
                        {selectedParty
                            ? selectedParty.name
                            : value && (partiesQuery.isLoading || selectedPartyQuery.isLoading)
                                ? "Loading..."
                                : placeholder}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[--radix-popover-content-available-height] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search party..."
                            onValueChange={debouncedSetSearchQuery}
                            disabled={disabled}
                        />
                        <CommandList>
                            {partiesQuery.isLoading && (
                                <div className="p-2 flex items-center justify-center">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                            )}
                            {partiesQuery.isError && (
                                <div className="p-2 text-center text-sm text-destructive flex items-center justify-center">
                                    <AlertTriangle className="h-4 w-4 mr-2" /> Error loading parties
                                </div>
                            )}
                            {!partiesQuery.isLoading && !partiesQuery.isError && parties.length === 0 && !searchQuery && (
                                <CommandEmpty>No parties found.</CommandEmpty>
                            )}
                            {!partiesQuery.isLoading && !partiesQuery.isError && parties.length === 0 && searchQuery && (
                                <CommandEmpty>No parties match &quot;{searchQuery}&quot;.</CommandEmpty>
                            )}

                            {!partiesQuery.isLoading && !partiesQuery.isError && (
                                <ScrollArea className="max-h-52">
                                    <CommandGroup>
                                        {displayParties.map((party) => (
                                            <CommandItem
                                                key={party.id}
                                                value={party.name}
                                                onSelect={() => {
                                                    onChange(party.id);
                                                    setOpen(false);
                                                    setSearchQuery('');
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        value === party.id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {party.name}
                                                {party.phone && <span className="ml-2 text-xs text-muted-foreground">{party.phone}</span>}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </ScrollArea>
                            )}
                            <CommandSeparator />
                            <CommandItem
                                onSelect={handleOpenPartyForm}
                                className="cursor-pointer group"
                            >
                                <PlusCircle className="mr-2 h-4 w-4 text-primary group-hover:text-primary-foreground" />
                                Create New Party
                            </CommandItem>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {isPartyFormOpen && (
                <PartyForm
                    open={isPartyFormOpen}
                    onOpenChange={(o) => {
                        setIsPartyFormOpen(o);
                        if (!o) createPartyMutation.reset();
                    }}
                    onSubmit={handleCreateNewPartySubmit}
                    isLoading={createPartyMutation.isPending || partyGroupsQuery.isLoading || sitesQuery.isLoading}
                    mode="create"
                    partyGroups={partyGroupsQuery.data?.items || []}
                    sites={sitesQuery.data?.items || []}
                    defaultSiteId={defaultSiteId}
                />
            )}
        </>
    );
}; 